<template>
  <v-dialog v-model="localShow" persistent max-width="850" @click:outside="cancel">
    <v-card>
      <v-card-text class="text-h6 pt-4">Confirm your name and signature</v-card-text>
      <v-card-text>
        <v-text-field
          label="Full Name"
          v-model="fullName"
          counter
          :rules="[requiredRule]"
          :maxlength="255"
        ></v-text-field>
      </v-card-text>
      <v-card-text>
        <v-tabs>
          <v-tab @click="creatingSignature = true">Create Signature</v-tab>
          <v-tab @click="creatingSignature = false">Draw</v-tab>
        </v-tabs>
      </v-card-text>
      <v-card-text v-show="creatingSignature" style="padding-bottom: 0 !important;">PREVIEW</v-card-text>
      <v-card-text v-show="creatingSignature">
        <canvas
          id="createSignatureCanvas"
          :width="signatureSize.width"
          :height="signatureSize.height"
          style="border: solid 1px #bbb;"
        ></canvas>
      </v-card-text>
      <v-card-text v-show="!creatingSignature" style="padding-bottom: 0 !important;">DRAW YOUR SIGNATURE</v-card-text>
      <v-card-text v-show="!creatingSignature">
        <vue-drawing-canvas
          ref="VueCanvasDrawing"
          v-model:image="imageAsBase64"
          :stroke-type="strokeType"
          :fill-shape="fillShape"
          :eraser="eraser"
          :lineWidth="line"
          :color="color"
          :background-color="backgroundColor"
          :background-image="backgroundImage"
          :watermark="watermark"
          saveAs="png"
          :styles="{
            border: 'solid 1px #bbb',
          }"
          :lock="disabled"
          :width="signatureSize.width"
          :height="signatureSize.height"
        />
      </v-card-text>
      <v-card-text>
        <v-row class="ma-1">
          {{ writtenNameAndDate }}
          <v-spacer />
          <v-btn @click.prevent="clear" v-show="!creatingSignature" variant="text">Clear</v-btn>
          <v-btn class="mr-2" @click="cancel" variant="text">Cancel</v-btn>
          <v-btn color="primary" @click="skipSignature" class="mx-2">Return unsigned</v-btn>
          <v-btn color="primary" :disabled="disableSignatureButton" @click="acceptClicked">Return</v-btn>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import VueDrawingCanvas from "vue-drawing-canvas";
import dateService from "@/services/dateService";
import userSignatureImageService from "@/services/userSignatureImageService";
import { mapState } from "vuex";
import Auth0Mixin from "../views/Auth0Mixin";
import ValidationRules from "../validationRules";
import WebFont from "webfontloader";

const IMAGE_BASE_PREFIX = "data:image/png;base64,";
let preloadedSignature = "";

export default {
  name: "SignatureDialog",
  components: {
    VueDrawingCanvas,
  },
  mixins: [Auth0Mixin],
  props: {
    signatureSize: Object,
    close: Function,
    accept: Function,
    skipSignature: Function,
    show: Boolean,
  },
  emits: ["update:show"],
  watch: {
    show(val) {
      this.localShow = val;
      if (val) {
        this.fullName = preloadedSignature?.fullName ?? "";
        this.backgroundImage = preloadedSignature?.signatureImageAsBase64 ?? "";
        if (this.$refs.VueCanvasDrawing) this.$refs.VueCanvasDrawing.reset();
      }
    },
    localShow(val) {
      if (!val) this.$emit("update:show", val);
    },
    fullName: async function(newVal) {
      // Get the canvas
      let canvas = document.getElementById("createSignatureCanvas");

      // Wait until the canvas is created and visible (offsetParent != null) in the dom before creating the signature
      if (canvas == null || canvas.offsetParent == null) canvas = await this.waitForElement("createSignatureCanvas");

      this.createSignature(newVal, canvas);
    },
  },
  data() {
    return {
      x: 0,
      y: 0,
      imageAsBase64: "",
      eraser: false,
      disabled: false,
      fillShape: false,
      line: 2,
      color: "#000000",
      strokeType: "dash",
      backgroundColor: "#FFFFFF",
      backgroundImage: null,
      watermark: null,
      creatingSignature: true,
      fullName: "",
      requiredRule: ValidationRules.requiredRule,
      localShow: this.show,
    };
  },

  computed: {
    ...mapState("auth0Store", ["user"]),
    ...mapState("userProfile", ["dateFormat"]),
    writtenNameAndDate() {
      if (!this.dateFormat || !this.user) return "";

      let formattedDate = dateService.formatDateTime(new Date(), this.dateFormat);
      return `${this.user.displayName} - ${formattedDate}`;
    },
    disableSignatureButton() {
      return (
        (this.imageAsBase64 !== "" &&
          this.$refs.VueCanvasDrawing.isEmpty() &&
          this.backgroundImage === "" &&
          !this.creatingSignature) ||
        this.fullName === ""
      );
    },
  },

  methods: {
    async init() {
      preloadedSignature = await userSignatureImageService.getUserSignature(this.user.id);

      WebFont.load({
        google: {
          families: ["Ms Madi"],
        },
      });
    },
    cancel() {
      this.$refs.VueCanvasDrawing.clear();
      this.fullName = "";
      this.close(false);
    },
    clear() {
      this.backgroundImage = "";
      this.$refs.VueCanvasDrawing.reset();
    },
    makeImageTransparent(image) {
      let canvas = "";
      let ctx = "";

      if (this.creatingSignature) {
        canvas = document.getElementById("createSignatureCanvas");
        ctx = canvas.getContext("2d");
      } else {
        canvas = document.createElement("canvas");
        canvas.width = this.signatureSize.width;
        canvas.height = this.signatureSize.height;

        ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0);
      }

      let imageData = ctx.getImageData(0, 0, this.signatureSize.width, this.signatureSize.height);
      let pix = imageData.data;

      // Loops through all of the pixels
      for (let i = 0, n = pix.length; i < n; i += 4) {
        // Pixel is white
        if (pix[i] == 255 && pix[i + 1] == 255 && pix[i + 2] == 255) {
          // Set transparency to 0
          pix[i + 3] = 0;
        }
      }

      ctx.putImageData(imageData, 0, 0);
      this.imageAsBase64 = canvas.toDataURL("image/png");
    },
    async persistSignature() {
      let image = this.imageAsBase64;
      // Do not update the drawn signature if a signature was created
      if (this.creatingSignature) image = preloadedSignature?.signatureImageAsBase64 ?? "";

      let signatureImage = {
        userId: this.user.id,
        signatureImageAsBase64: image,
        fullName: this.fullName,
      };
      if (preloadedSignature === "") {
        await userSignatureImageService.saveUserSignatureImage(signatureImage);
      } else {
        await userSignatureImageService.updateUserSignatureImage(signatureImage);
      }
    },
    async acceptClicked() {
      if (this.$refs.VueCanvasDrawing.isEmpty() && !this.creatingSignature) {
        this.imageAsBase64 = this.backgroundImage;
        this.accept(this.imageAsBase64.substring(IMAGE_BASE_PREFIX.length));
      } else {
        let image = new Image();
        image.onload = () => {
          this.makeImageTransparent(image);
          this.persistSignature();
          this.accept(this.imageAsBase64.substring(IMAGE_BASE_PREFIX.length));
        };
        image.src = this.imageAsBase64;
      }
    },
    createSignature(value, canvas) {
      let ctx = canvas.getContext("2d");

      // Set the font size to be 40% of the canvas height
      let fontSize = Math.floor(canvas.height * 0.4);
      ctx.font = fontSize + "px Ms Madi";

      // Clear the canvas and the draw the text
      let padding = canvas.width / 40;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillText(value, padding, canvas.height * 0.6, canvas.width - padding * 3);
    },
    waitForElement(elementId) {
      return new Promise(resolve => {
        const observer = new MutationObserver(() => {
          let element = document.getElementById(elementId);
          if (element && element.offsetParent !== null) {
            observer.disconnect();
            resolve(document.getElementById(elementId));
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });
      });
    },
  },
};
</script>
